import { ACTOR_ID_HEADER_KEY, CLIENT_NAME_HEADER_VALUE, O_API_KEY } from "constants";
import apiClient from "utils/apiClient";

const WORKSPACES_BASE = "/identity-service/api/v1/workspaces";
const ROLES_BASE = "/identity-service/api/v1/roles";
const PRIVILEGES_BASE = "/identity-service/api/v1/privileges";
const THIS_APP_WORKSPACE_PRODUCT = CLIENT_NAME_HEADER_VALUE;
const SKIP_WORKSPACE_CONTEXT_HEADER = "O-SKIP-WORKSPACE-CONTEXT";
const OLITASK_RBAC_CODE = "OLITASK";

const pageFrom = (data, fallbackContent = []) => {
  if (Array.isArray(data)) {
    return { content: data };
  }

  return {
    ...data,
    content: Array.isArray(data?.content) ? data.content : fallbackContent,
  };
};

const actorHeaders = (actorId) =>
  actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};

const workspaceServiceHeaders = (actorId) => ({
  ...actorHeaders(actorId),
  [SKIP_WORKSPACE_CONTEXT_HEADER]: "1",
});

const apiKeyHeaders = () => ({ "Api-Key": O_API_KEY });

const normalizeRbacKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

export const isOlitaskProductRbac = (item) =>
  normalizeRbacKey(item?.type) === "PRODUCT" &&
  normalizeRbacKey(item?.owner) === "OLIVEEB_OLITASK_SERVICE";

const inviteRbacItems = (data) =>
  (Array.isArray(data) ? data : data?.content || []).filter(
    (item) => item?.id && isOlitaskProductRbac(item),
  );

const normalizeMembership = (membership) => {
  if (!membership || typeof membership !== "object") {
    return membership;
  }

  return {
    ...membership,
    workspaceId: membership.workspaceId,
  };
};

const normalizeWorkspace = (workspace) => {
  if (!workspace || typeof workspace !== "object") {
    return workspace || {};
  }

  return {
    ...workspace,
    organizationId: workspace.organizationId,
    users: Array.isArray(workspace.users)
      ? workspace.users.map(normalizeMembership)
      : workspace.users,
  };
};

const normalizeWorkspacePage = (data) => {
  const page = pageFrom(data);
  return {
    ...page,
    content: (page.content || []).map(normalizeWorkspace),
  };
};

const normalizeMembershipPage = (data) => {
  const page = pageFrom(data);
  return {
    ...page,
    content: (page.content || []).map(normalizeMembership),
  };
};

const propertyUpdate = (name, value) => ({
  name,
  value: value === undefined || value === null ? null : String(value),
});

export const fetchMemberWorkspaces = async ({
  actorId,
  userId,
  page = 0,
  size = 20,
  sort = "createdAt,desc",
} = {}) => {
  const params = { page, size, sort, product: THIS_APP_WORKSPACE_PRODUCT };
  if (userId && userId !== actorId) {
    params.userId = userId;
  }

  const response = await apiClient.get(`${WORKSPACES_BASE}/member`, {
    params,
    headers: workspaceServiceHeaders(actorId),
  });

  return normalizeWorkspacePage(response.data);
};

export const fetchWorkspace = async (workspaceId, actorId) => {
  const response = await apiClient.get(`${WORKSPACES_BASE}/${workspaceId}`, {
    headers: workspaceServiceHeaders(actorId),
  });

  return normalizeWorkspace(response.data);
};

export const createWorkspace = async ({
  actorId,
  name,
  description,
  accountType = "PERSONAL",
  organizationId,
  workspaceUse,
  subscriptionId,
} = {}) => {
  const payload = {
    name,
    description,
    accountType,
    product: THIS_APP_WORKSPACE_PRODUCT,
  };
  if (organizationId) payload.organizationId = organizationId;
  if (workspaceUse) payload.workspaceUse = workspaceUse;
  if (subscriptionId) payload.subscriptionId = subscriptionId;

  const response = await apiClient.post(WORKSPACES_BASE, payload, {
    headers: workspaceServiceHeaders(actorId),
  });

  return normalizeWorkspace(response.data);
};

export const updateWorkspaceProperties = async ({
  actorId,
  workspaceId,
  updates = [],
} = {}) => {
  const response = await apiClient.patch(
    `${WORKSPACES_BASE}/${workspaceId}/props`,
    updates,
    { headers: actorHeaders(actorId) },
  );

  return normalizeWorkspace(response.data);
};

export const updateWorkspace = async ({
  actorId,
  workspaceId,
  name,
  description,
  accountType,
  organizationId,
  subscriptionId,
} = {}) => {
  const updates = [];
  if (name !== undefined) updates.push(propertyUpdate("name", name));
  if (description !== undefined) updates.push(propertyUpdate("description", description));
  if (accountType !== undefined) updates.push(propertyUpdate("accountType", accountType));
  if (organizationId !== undefined) {
    updates.push(propertyUpdate("organizationId", organizationId));
  }
  if (subscriptionId !== undefined) {
    updates.push(propertyUpdate("subscriptionId", subscriptionId));
  }

  return updateWorkspaceProperties({ actorId, workspaceId, updates });
};

export const deleteWorkspace = async ({ actorId, workspaceId } = {}) => {
  const response = await apiClient.delete(`${WORKSPACES_BASE}/${workspaceId}`, {
    headers: actorHeaders(actorId),
  });

  return response.data || {};
};

export const fetchWorkspaceMembers = async ({
  actorId,
  workspaceId,
  status,
  page = 0,
  size = 100,
  sort = "createdAt,desc",
} = {}) => {
  const params = { page, size, sort };
  if (status) {
    params.status = status;
  }

  const response = await apiClient.get(
    `${WORKSPACES_BASE}/${workspaceId}/members`,
    {
      params,
      headers: actorHeaders(actorId),
    },
  );

  return normalizeMembershipPage(response.data);
};

export const removeWorkspaceMember = async ({
  actorId,
  workspaceId,
  membershipId,
} = {}) => {
  const response = await apiClient.delete(
    `${WORKSPACES_BASE}/${workspaceId}/members/${membershipId}`,
    { headers: actorHeaders(actorId) },
  );

  return normalizeMembership(response.data) || {};
};

export const fetchWorkspaceMembershipsForUser = async ({
  actorId,
  userId,
  page = 0,
  size = 100,
  sort = "createdAt,desc",
} = {}) => {
  const response = await apiClient.get(
    `${WORKSPACES_BASE}/user/${userId}/memberships`,
    {
      params: { page, size, sort },
      headers: actorHeaders(actorId),
    },
  );

  return normalizeMembershipPage(response.data);
};

export const respondToWorkspaceInvitation = async ({
  actorId,
  workspaceId,
  action,
} = {}) => {
  const response = await apiClient.post(
    `${WORKSPACES_BASE}/${workspaceId}/invitations/${action}`,
    undefined,
    { headers: actorHeaders(actorId) },
  );

  return normalizeMembership(response.data) || {};
};

export const inviteWorkspaceUser = async ({
  actorId,
  workspaceId,
  email,
  userId,
  roleIds,
  privilegeIds,
} = {}) => {
  const payload = {};
  if (email) payload.email = email;
  if (userId) payload.userId = userId;
  if (roleIds?.length) payload.roleIds = roleIds;
  if (privilegeIds?.length) payload.privilegeIds = privilegeIds;

  const response = await apiClient.post(
    `${WORKSPACES_BASE}/${workspaceId}/invitations/invite`,
    payload,
    { headers: actorHeaders(actorId) },
  );

  return normalizeMembership(response.data) || {};
};

export const withdrawWorkspaceInvitation = async ({
  actorId,
  workspaceId,
  invitationId,
} = {}) => {
  const response = await apiClient.post(
    `${WORKSPACES_BASE}/${workspaceId}/invitations/${invitationId}/withdraw`,
    undefined,
    { headers: actorHeaders(actorId) },
  );

  return normalizeMembership(response.data) || {};
};

export const updateWorkspaceSubscription = async ({
  actorId,
  workspaceId,
  subscriptionId,
} = {}) => {
  const response = await apiClient.patch(
    `${WORKSPACES_BASE}/${workspaceId}/subscription`,
    { subscriptionId },
    { headers: actorHeaders(actorId) },
  );

  return normalizeWorkspace(response.data);
};

export const updateWorkspaceOrganization = async ({
  actorId,
  workspaceId,
  organizationId,
} = {}) => {
  return updateWorkspace({
    actorId,
    workspaceId,
    organizationId,
  });
};

export const fetchWorkspaceInviteRoles = async () => {
  const response = await apiClient.get(ROLES_BASE, {
    params: { code: OLITASK_RBAC_CODE },
    headers: apiKeyHeaders(),
  });
  return inviteRbacItems(response.data);
};

export const fetchWorkspaceInvitePrivileges = async () => {
  const response = await apiClient.get(PRIVILEGES_BASE, {
    params: { code: OLITASK_RBAC_CODE },
    headers: apiKeyHeaders(),
  });
  return inviteRbacItems(response.data);
};
